/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef, ChangeEventHandler, useCallback, useLayoutEffect } from "react";
import { DayPicker, CustomComponents, DayModifiers } from "react-day-picker";
import { useFormContext, get, useController } from "react-hook-form";
import moment from "moment";
import { useIntl, FormattedMessage } from "react-intl";

import "react-day-picker/dist/style.css";
import "./DatePicker.scss";
import { ReactComponent as Calendar } from "assets/images/icons/Calendar.svg";
import { ReactComponent as AnchorLeft } from "assets/images/icons/LeftPolygon.svg";
import { ReactComponent as AnchorRight } from "assets/images/icons/RightPolygon.svg";
import OverlayLoader from "../OverlayLoader/OverlayLoader";

export type DatePickerProps = {
  name: string;
  inputFormat?: string;
  outputFormat?: string;
  required?: boolean;
  validations?: object;
  hideValidationError?: boolean;
  registerOptions?: object;
  minDate?: Date;
  maxDate?: Date;
  wrapperClassNames?: string;
  isLoading?: boolean;
  modifiers?: DayModifiers;
  components?: CustomComponents;
  shouldUnregister?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  onDateSelect?: (date?: Date) => void;
  onMonthChange?: (date?: Date) => void;
  onOpen?: (date: Date) => void;
};

function DatePicker(props: DatePickerProps) {
  const intl = useIntl();
  const {
    name,
    required,
    validations,
    hideValidationError,
    registerOptions,
    inputFormat,
    outputFormat,
    minDate,
    maxDate,
    isLoading,
    modifiers,
    components,
    wrapperClassNames,
    defaultValue,
    disabled,
    shouldUnregister,
    onOpen = () => {},
    onDateSelect = () => {},
    onMonthChange = () => {},
  } = props;
  const [showPicker, setShowPicker] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [showPickerUpward, setShowPickerUpward] = useState(false);
  const [month, setMonth] = useState<Date>(new Date());
  const pickerContainerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<any>(null);
  const { control, getValues } = useFormContext();
  const {
    field,
    formState: { errors },
  } = useController({
    name,
    control,
    shouldUnregister,
    ...(defaultValue && { defaultValue }),
    rules: {
      required,
      validate: {
        _dpValidDate: (val) =>
          isValidDate(val) ||
          intl.formatMessage({ id: "validation.validDate", defaultMessage: "Please enter a valid date" }),
        _dpDisabledDate: (val) =>
          isDateSelectable(val) ||
          intl.formatMessage({
            id: "validation.selectableDate",
            defaultMessage: "Selected date is in the past, please select a date in the future",
          }),
        ...validations,
      },
      ...registerOptions,
    },
  });

  useEffect(() => {
    function handleOutsideClick(e: any) {
      if (!pickerContainerRef.current?.contains(e.target)) {
        setShowPicker(false);
      }
    }
    window.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (field.value) {
      const date = moment(field.value, outputFormat, true);
      setInputValue(date.isValid() ? date.format(inputFormat) : field.value);
      if (date.isValid()) {
        setMonth(date.toDate());
      }
    } else {
      setInputValue("");
    }
  }, [field.value, inputFormat, outputFormat]);

  const handleDatePickerPosition = useCallback(
    (element: any) => {
      if (popupRef.current && showPicker) {
        const rect = popupRef.current.getBoundingClientRect();
        const bottom = showPickerUpward ? rect.bottom + 367 + 30 : rect.bottom + 10;
        const touchesBottom = bottom > element.offsetHeight;
        if (showPickerUpward !== touchesBottom) {
          setShowPickerUpward(touchesBottom);
        }
      }
    },
    [showPicker, showPickerUpward],
  );

  useEffect(() => {
    const element: any = document.querySelector(".App");
    const handleScroll = () => {
      handleDatePickerPosition(element);
    };
    element?.addEventListener("scroll", handleScroll);
    return () => {
      element?.removeEventListener("scroll", handleScroll);
    };
  }, [handleDatePickerPosition]);

  useLayoutEffect(() => {
    if (showPicker) {
      const element: any = document.querySelector(".App");
      handleDatePickerPosition(element);
    }
  }, [showPicker]);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (disabled) return;
    setInputValue(e.target.value);
    if (!e.target.value) {
      field.onChange("");
      onDateSelect();
    } else {
      const parsedValue = parseDateValue(e.target.value);
      const date = moment(parsedValue, inputFormat, true);
      field.onChange(date.isValid() ? date.format(outputFormat) : parsedValue);
      if (date.isValid()) {
        setMonth(date.toDate());
        onDateSelect(date.toDate());
      }
    }
  };

  function parseDateValue(val: any) {
    if (val.length === 8 && !isNaN(val)) {
      const [firstEnd, secondEnd] = getDateSplitIndexes(inputFormat?.split("-") || []);
      return `${val.slice(0, firstEnd)}-${val.slice(firstEnd, secondEnd)}-${val.slice(secondEnd)}`;
    }
    return val;
  }

  function getDateSplitIndexes(arr: Array<string>) {
    return [arr[0].length, arr[0].length + arr[1].length];
  }

  const handleDaySelect = (date?: Date) => {
    if (date) {
      field.onChange(moment(date).format(outputFormat));
      onDateSelect(date);
      setShowPicker(false);
    } else {
      field.onChange("");
      onDateSelect();
    }
  };

  // When opening, make sure DayPicker visualizes the month of the date in the
  // input field.
  const setCurrentInputMonth = () => {
    if (!inputValue) {
      return;
    }
    const day = moment(inputValue, inputFormat, true);
    if (day.isValid()) {
      setMonth(day.toDate());
    }
  };

  const handleCalendarClick = () => {
    if (!showPicker) {
      setCurrentInputMonth();
      onOpen(month);
    }
    setShowPicker(true);
  };

  const isDateSelectable = (date: Date) => {
    if (
      (maxDate && moment(date).isAfter(maxDate)) ||
      (minDate && moment(`${moment(date).format(outputFormat)} 23:59`, `${outputFormat} HH:mm`).isBefore(minDate))
    ) {
      return false;
    }
    return true;
  };

  const handleDayAdd = () => {
    const date = moment(getValues(name), outputFormat, true);
    if (!date.isValid() || disabled) {
      return;
    }
    const nextDate = moment(date).add(1, "day");
    if (!isDateSelectable(nextDate.toDate())) {
      return;
    }
    field.onChange(nextDate.format(outputFormat));
    onDateSelect(nextDate.toDate());
  };

  const handleDaySubtract = () => {
    const date = moment(getValues(name), outputFormat, true);
    if (!date.isValid() || disabled) {
      return;
    }
    const previousDate = moment(date).subtract(1, "day");
    if (!isDateSelectable(previousDate.toDate())) {
      return;
    }
    field.onChange(previousDate.format(outputFormat));
    onDateSelect(previousDate.toDate());
  };

  const canNavigatePrevious = () => {
    const date = moment(inputValue, inputFormat, true);
    return date.isValid() && isDateSelectable(date.subtract(1, "day").toDate()) && !disabled;
  };

  const canNavigateNext = () => {
    const date = moment(inputValue, inputFormat, true);
    return date.isValid() && isDateSelectable(date.add(1, "day").toDate()) && !disabled;
  };

  const isValidDate = (val: string) => !val || moment(val, outputFormat, true).isValid();

  return (
    <div className={wrapperClassNames}>
      <div
        className={`date-picker-wrapper ${get(errors, name) ? "border-danger" : ""} ${
          moment(inputValue, inputFormat, true).isValid() ? "with-icons" : ""
        }`}
        ref={pickerContainerRef}
      >
        <AnchorLeft
          className={`cursor-pointer ${!moment(inputValue, inputFormat, true).isValid() ? "hidden" : ""} ${
            !canNavigatePrevious() ? "disabled" : ""
          }`}
          onClick={handleDaySubtract}
        />
        <Calendar className="cursor-pointer" width={28} height={28} onClick={handleCalendarClick} />
        {moment(inputValue, inputFormat, true).isValid() && (
          <span>{moment(inputValue, inputFormat, true).format("ddd,")}</span>
        )}
        <input
          autoComplete="off"
          className="date-picker-input"
          placeholder={inputFormat}
          value={inputValue}
          onChange={handleInputChange}
          ref={field.ref}
          onBlur={field.onBlur}
          name={field.name}
          onClick={handleCalendarClick}
        />
        <AnchorRight
          className={`cursor-pointer ml-auto ${!moment(inputValue, inputFormat, true).isValid() ? "hidden" : ""} ${
            !canNavigateNext() ? "disabled" : ""
          }`}
          onClick={handleDayAdd}
        />
        <div
          ref={popupRef}
          className={`calendar-container ${showPicker ? "show" : ""} ${
            showPickerUpward ? "container-top" : "container-bottom"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <DayPicker
            mode="single"
            month={month}
            onMonthChange={(date) => {
              setMonth(date);
              onMonthChange(date);
            }}
            fromDate={minDate}
            disabled={disabled}
            toDate={maxDate}
            modifiers={modifiers}
            components={components}
            modifiersClassNames={{
              selected: "calendar-selected-item",
            }}
            selected={
              moment(field.value, outputFormat, true).isValid()
                ? moment(field.value, outputFormat, true).toDate()
                : undefined
            }
            onSelect={handleDaySelect}
          />
          {isLoading && <OverlayLoader />}
        </div>
      </div>
      {!hideValidationError && get(errors, name) && (
        <div className="d-block font-b3 invalid-feedback">
          {get(errors, name)?.message ? (
            get(errors, name).message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      )}
    </div>
  );
}

export default DatePicker;

DatePicker.defaultProps = {
  required: false,
  hideValidationError: false,
  validations: undefined,
  registerOptions: undefined,
  inputFormat: "DD-MM-YYYY",
  outputFormat: "YYYY-MM-DD",
  isLoading: false,
  onDateSelect: undefined,
  onMonthChange: undefined,
  modifiers: undefined,
  components: undefined,
  onOpen: undefined,
  minDate: undefined,
  maxDate: undefined,
  wrapperClassNames: "",
  shouldUnregister: false,
  defaultValue: undefined,
  disabled: false,
};
